import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { RotateCcw, Search, XIcon } from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetList } from "../../../../hooks/useGetList";
import {
  resetAll,
  setFilter,
  setSearch,
} from "../../../../redux/stockCountAndPrice/stockCountAndPriceSlice";
import debounce from "../../../../Utils/debounce";

const StockCountAndPriceHeader = () => {
  const inputRef = useRef();
  const filter = useSelector((state) => state.filterStockCountAndPrice);
  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);
  const {
    data: productItemData,
    loading: productItemLoading,
    fetchData: fetchProductItem,
  } = useGetList(
    `product-item/options`,
    filter.filterType?.relatedBranch ? true : false,
    {
      isPopulateUnits: "true",
      relatedBranch: filter.filterType?.relatedBranch,
    },
  );
  const dispatch = useDispatch();
  const handleSearchChange = debounce(() => {
    if (inputRef.current) {
      dispatch(setSearch(inputRef.current.value));
    }
  }, 500);

  const handleBranchChange = (event) => {
    if (event.target.value === "All") {
      dispatch(setFilter({ relatedBranch: "All" }));
    } else {
      dispatch(setFilter({ relatedBranch: event.target.value }));
    }
  };
  const handleClearSearch = () => {
    dispatch(setSearch(""));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleProductItemChange = (event, newValue) => {
    console.log(newValue);
    dispatch(setFilter({ relatedItem: newValue ? newValue.value : "All" }));
  };

  const productItemOptions = useMemo(
    () =>
      productItemData?.data?.map((item) => ({
        value: item._id,
        label: item.medicineItemName,
      })) || [],
    [productItemData],
  );

  const selectedProductItem = useMemo(
    () =>
      productItemOptions.find(
        (opt) => opt.value === filter.filterType?.relatedItem,
      ) || null,
    [filter.filterType?.relatedItem, productItemOptions],
  );

  const filterItemUnits = productItemData?.data?.find(
    (item) => item._id === filter.filterType?.relatedItem,
  )?.relatedItemUnits;

  console.log(filterItemUnits, "itemUnit");

  const handleUnitChange = (event) => {
    const val = event.target.value;
    dispatch(setFilter({ relatedUnit: val === "All" ? "All" : val }));
  };

  const hasActiveFilters =
    filter?.keyword ||
    (filter?.filterType?.relatedBranch &&
      filter?.filterType?.relatedBranch !== "All") ||
    filter?.filterType?.relatedItem ||
    (filter?.filterType?.relatedUnit &&
      filter?.filterType?.relatedUnit !== "All");

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    dispatch(resetAll());
  };

  useEffect(() => {
    if (branchData?.data?.length > 0) {
      dispatch(setFilter({ relatedBranch: branchData?.data[0]?._id }));
    }
  }, [branchData]);

  useEffect(() => {
    if (filter?.filterType?.relatedBranch) {
      fetchProductItem({
        isPopulateUnits: "true",
        relatedBranch: filter.filterType?.relatedBranch,
      });
    }
  }, [filter]);

  return (
    <div>
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Field */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                inputRef={inputRef}
                placeholder="Search..."
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: inputRef.current?.value ? (
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ ml: 1 }}
                    >
                      <XIcon fontSize="small" size={12} />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Branch Filter */}
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={filter.filterType?.relatedBranch ?? "All"}
                  onChange={handleBranchChange}
                  label="Branch"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="All">All Branches</MenuItem>
                  {!branchLoading &&
                    branchData?.data?.map((branch) => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Product Item Filter */}
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                options={productItemOptions}
                value={selectedProductItem}
                onChange={handleProductItemChange}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Item"
                    placeholder="Search Product Item..."
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                )}
                size="small"
                noOptionsText="No product items found"
              />
            </Grid>

            {/* Unit Filter + Reset */}
            <Grid item xs={12} md={2.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={filter.filterType?.relatedUnit ?? "All"}
                    onChange={handleUnitChange}
                    label="Unit"
                    sx={{ borderRadius: 2 }}
                    disabled={filterItemUnits?.length === 0}
                  >
                    <MenuItem value="All">All Units</MenuItem>
                    {filterItemUnits?.map((unit) => (
                      <MenuItem key={unit._id} value={unit._id}>
                        {unit.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title={hasActiveFilters ? "Reset filters" : "No active filters"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleResetFilters}
                      disabled={!hasActiveFilters}
                      color="primary"
                      sx={{ borderRadius: 2, flexShrink: 0 }}
                    >
                      <RotateCcw size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockCountAndPriceHeader;
